import { useMemo, useState } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Alert,
  Button,
  Card,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Table,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconDeviceTv, IconShieldLock } from '@tabler/icons-react';
import { ApiError } from '@/shared/api/ApiError';
import { AppUtils } from '../AppUtils';
import { AppFactoryUtils } from '../AppFactoryUtils';
import {
  useFactoryCampaigns,
  useFactoryCampaignTargets,
  useFactoryDevices,
  useFactoryReleases,
  useAdvanceCampaign,
  useAbortCampaign,
  useTransitionSubmission,
} from '../useAppFactory';
import type { FactoryTarget } from '@/lib/types/AppFactory';
import { TRANSITIONABLE_STATES, legalNextStates } from './submissionStates';

/**
 * Operator campaign console (fleet migration). Reached by URL only — there is
 * deliberately no nav entry and no client-side role: the backend's operator
 * allowlist is the authority and everyone else gets a 403 here.
 */
export function OperatorConsolePage() {
  const campaignsQuery = useFactoryCampaigns();
  const devicesQuery = useFactoryDevices();
  const releasesQuery = useFactoryReleases();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const targetsQuery = useFactoryCampaignTargets(selectedCampaignId);

  const notAuthorized =
    campaignsQuery.error instanceof ApiError && campaignsQuery.error.httpStatusCode === 403;

  if (campaignsQuery.isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Loading operator console...
        </Text>
      </Group>
    );
  }

  if (notAuthorized) {
    return (
      <Stack>
        <Title order={2}>App Center Operations</Title>
        <Alert icon={<IconShieldLock size={16} />} color="orange">
          Not authorized. This console is limited to platform operator accounts.
        </Alert>
      </Stack>
    );
  }

  if (campaignsQuery.isError) {
    return (
      <Stack>
        <Title order={2}>App Center Operations</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="gray">
          The app factory is currently unreachable.
        </Alert>
      </Stack>
    );
  }

  const campaigns = AppFactoryUtils.sortByCreatedDesc(campaignsQuery.data?.campaigns ?? []);
  const devices = devicesQuery.data?.devices ?? [];
  const releases = releasesQuery.data?.releases ?? [];
  const releaseById = new Map(releases.map((r) => [r.shellReleaseId, r]));

  return (
    <Stack gap="lg">
      <Title order={2}>App Center Operations</Title>

      {/* Farm health */}
      <Stack gap="xs">
        <Text fw={600} size="md">
          Farm Devices
        </Text>
        {devices.length === 0 ? (
          <Text size="sm" c="dimmed">
            No farm devices registered.
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {devices.map((d) => (
              <Card key={d.farmDeviceId} withBorder radius="md" p="md">
                <Group gap="xs">
                  <IconDeviceTv size={18} />
                  <Text fw={600} size="sm">
                    {d.name}
                  </Text>
                  <Badge
                    variant="light"
                    size="sm"
                    color={d.state === 'ready' ? 'green' : d.state === 'claimed' ? 'blue' : 'orange'}
                  >
                    {d.state}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed" mt={4}>
                  {d.host}
                  {d.osVersion ? ` · OS ${d.osVersion}` : ''}
                </Text>
                {d.claimedAt && (
                  <Text size="xs" c="dimmed">
                    Claimed {AppFactoryUtils.formatWhen(d.claimedAt)}
                  </Text>
                )}
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* Campaigns */}
      <Stack gap="xs">
        <Text fw={600} size="md">
          Campaigns
        </Text>
        {campaigns.length === 0 ? (
          <Text size="sm" c="dimmed">
            No campaigns yet. Campaigns are created through the factory ops tooling.
          </Text>
        ) : (
          <Paper withBorder style={{ overflow: 'clip' }}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Release</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {campaigns.map((c) => (
                  <Table.Tr
                    key={c.appCampaignId}
                    style={{ cursor: 'pointer' }}
                    bg={
                      selectedCampaignId === c.appCampaignId
                        ? 'var(--mantine-color-blue-0)'
                        : undefined
                    }
                    onClick={() => setSelectedCampaignId(c.appCampaignId)}
                  >
                    <Table.Td>{c.name}</Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        size="sm"
                        color={
                          c.state === 'running'
                            ? 'blue'
                            : c.state === 'complete'
                              ? 'green'
                              : c.state === 'aborted'
                                ? 'red'
                                : 'gray'
                        }
                      >
                        {c.state}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{releaseById.get(c.shellReleaseId)?.semver ?? c.shellReleaseId}</Table.Td>
                    <Table.Td>{AppFactoryUtils.formatWhen(c.created)}</Table.Td>
                    <Table.Td>
                      <CampaignActions
                        campaignId={c.appCampaignId}
                        state={c.state}
                        name={c.name}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      {/* Targets for the selected campaign */}
      {selectedCampaignId != null && (
        <CampaignTargets
          isLoading={targetsQuery.isLoading}
          targets={targetsQuery.data?.targets ?? []}
        />
      )}

      <RecordTransitionForm />
    </Stack>
  );
}

// ── Internal: advance/abort actions ──

function CampaignActions({
  campaignId,
  state,
  name,
}: {
  campaignId: string;
  state: string;
  name: string;
}) {
  const advanceMutation = useAdvanceCampaign();
  const abortMutation = useAbortCampaign();
  const actionable = state === 'draft' || state === 'running';
  if (!actionable) return null;

  const confirmAdvance = (e: React.MouseEvent) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: 'Advance campaign ring',
      children: (
        <Text size="sm">
          Advance “{name}” to its next ring? This enqueues builds for every target in that
          ring.
        </Text>
      ),
      labels: { confirm: 'Advance', cancel: 'Cancel' },
      onConfirm: () =>
        advanceMutation.mutate(campaignId, {
          onSuccess: () =>
            notifications.show({ title: 'Campaign advanced', message: name, color: 'green' }),
          onError: (err) =>
            notifications.show({ title: 'Error', message: err.message, color: 'red' }),
        }),
    });
  };

  const confirmAbort = (e: React.MouseEvent) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: 'Abort campaign',
      children: (
        <Text size="sm">Abort “{name}”? In-flight builds finish; nothing new is enqueued.</Text>
      ),
      labels: { confirm: 'Abort campaign', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        abortMutation.mutate(campaignId, {
          onSuccess: () =>
            notifications.show({ title: 'Campaign aborted', message: name, color: 'orange' }),
          onError: (err) =>
            notifications.show({ title: 'Error', message: err.message, color: 'red' }),
        }),
    });
  };

  return (
    <Group gap="xs" justify="flex-end" wrap="nowrap">
      <Button size="compact-xs" variant="light" onClick={confirmAdvance}>
        Advance
      </Button>
      <Button size="compact-xs" variant="light" color="red" onClick={confirmAbort}>
        Abort
      </Button>
    </Group>
  );
}

// ── Internal: targets table with fleet-completion summary ──

function CampaignTargets({
  isLoading,
  targets,
}: {
  isLoading: boolean;
  targets: FactoryTarget[];
}) {
  const [stateFilter, setStateFilter] = useState<string | null>(null);

  const stateCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of targets) counts.set(t.state, (counts.get(t.state) ?? 0) + 1);
    return counts;
  }, [targets]);

  const filtered = stateFilter ? targets.filter((t) => t.state === stateFilter) : targets;

  if (isLoading) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <div>
          <Text fw={600} size="md">
            Targets
          </Text>
          <Group gap="xs" mt={4}>
            <Badge variant="outline" color="gray" size="sm">
              {targets.length} total
            </Badge>
            {[...stateCounts.entries()].map(([state, n]) => (
              <Badge
                key={state}
                variant="light"
                size="sm"
                color={state === 'awaiting_customer' ? 'orange' : state === 'failed' || state === 'rejected' ? 'red' : 'gray'}
              >
                {state}: {n}
              </Badge>
            ))}
          </Group>
        </div>
        <Select
          placeholder="Filter by state"
          clearable
          data={[...stateCounts.keys()].sort()}
          value={stateFilter}
          onChange={setStateFilter}
          w={220}
        />
      </Group>

      <Paper withBorder style={{ overflow: 'clip' }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>App</Table.Th>
              <Table.Th>Platform</Table.Th>
              <Table.Th>Ring</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Blocked Reason</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.map((t) => (
              <Table.Tr key={t.appCampaignTargetId}>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {AppUtils.getDisplayId(t.appCfgId)}
                  </Text>
                </Table.Td>
                <Table.Td>{t.platform}</Table.Td>
                <Table.Td>{t.ring}</Table.Td>
                <Table.Td>
                  <Badge
                    variant="light"
                    size="sm"
                    color={
                      t.state === 'awaiting_customer'
                        ? 'orange'
                        : t.state === 'failed' || t.state === 'rejected'
                          ? 'red'
                          : t.state === 'live'
                            ? 'green'
                            : 'gray'
                    }
                  >
                    {t.state}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {t.blockedReason ?? ''}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

// ── Internal: record a manual store-side submission transition ──

function RecordTransitionForm() {
  const transitionMutation = useTransitionSubmission();
  const [submissionId, setSubmissionId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');

  const canSubmit = submissionId.trim() !== '' && from.trim() !== '' && to.trim() !== '';

  const handleSubmit = () => {
    transitionMutation.mutate(
      {
        submissionId: submissionId.trim(),
        from: from.trim(),
        to: to.trim(),
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Transition recorded',
            message: `Submission ${submissionId}: ${from} → ${to}`,
            color: 'green',
          });
          setSubmissionId('');
          setFrom('');
          setTo('');
          setNote('');
        },
        onError: (err) =>
          notifications.show({ title: 'Error', message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Card withBorder radius="md" p="md">
      <Text fw={600} size="md" mb="xs">
        Record Submission Transition
      </Text>
      <Text size="sm" c="dimmed" mb={4}>
        Manual Roku store steps are recorded here — the store has no API. State order:
      </Text>
      <Text size="sm" mb={2} ff="monospace">
        preparing → awaiting_customer → submitting → in_review → approved → live → superseded
      </Text>
      <Text size="sm" c="dimmed" mb="md" ff="monospace">
        in_review → rejected (terminal)
      </Text>
      <Group align="flex-end" wrap="wrap" gap="sm">
        <TextInput label="Submission ID" value={submissionId} onChange={(e) => setSubmissionId(e.currentTarget.value)} w={180} />
        <Select
          label="From"
          placeholder="Current state"
          data={TRANSITIONABLE_STATES as unknown as string[]}
          value={from || null}
          onChange={(v) => {
            setFrom(v ?? '');
            setTo('');
          }}
          w={180}
        />
        <Select
          label="To"
          placeholder={from ? 'Next state' : 'Pick From first'}
          data={legalNextStates(from) as unknown as string[]}
          value={to || null}
          onChange={(v) => setTo(v ?? '')}
          disabled={!from}
          w={180}
        />
        <TextInput label="Note" value={note} onChange={(e) => setNote(e.currentTarget.value)} style={{ flex: 1, minWidth: 200 }} />
        <Button onClick={handleSubmit} disabled={!canSubmit} loading={transitionMutation.isPending}>
          Record
        </Button>
      </Group>
    </Card>
  );
}
